import { HTMLCustomElement } from "@theme/html-custom-element";

class SearchDrawer extends HTMLCustomElement {
  connectedCallback() {
    if (super.connectedCallback) super.connectedCallback();

    this.input = this.querySelector(".header-search-form__input");
    this.results = this.querySelector(".search-results");
    this.drawer = this.querySelector("#search-drawer");
    this.clearButton = this.querySelector('[data-ref="search-drawer.clear"]');
    this.viewAll = this.querySelector('[data-ref="search-drawer.viewAll"]');
    this.defaultViewAllText = this.viewAll?.textContent.trim() ?? "";

    if (!this.input || !this.results || !this.drawer) return;

    this.updateQueryState(this.input.value.trim());
    this.on("input", this.input, this.handleInput.bind(this));
    if (this.clearButton) {
      this.on("click", this.clearButton, this.handleClear.bind(this));
    }

    if (this.viewAll) {
      this.on("click", this.viewAll, this.handleViewAllClick.bind(this));
    }

    this.on("click", this.results, this.handleResultsClick.bind(this));
    this.on("open", this.drawer, this.handleOpen.bind(this));
  }

  handleViewAllClick(event) {
    if (!this.countFailed) return;

    event.preventDefault();

    const query = this.input.value.trim();
    void this.fetchResults(query);
    void this.fetchCount(query);
  }

  setCountFailed(failed) {
    this.countFailed = failed;
    this.viewAll.textContent = failed ? this.retryText : this.defaultViewAllText;
  }

  handleResultsClick(event) {
    const tab = event.target.closest("[data-search-tab]");
    if (!tab || !this.results.contains(tab)) return;

    this.activateTab(tab.getAttribute("data-search-tab"));
  }

  activateTab(name) {
    for (const tab of this.results.querySelectorAll("[data-search-tab]")) {
      tab.setAttribute(
        "aria-selected",
        String(tab.getAttribute("data-search-tab") === name),
      );
    }

    for (const panel of this.results.querySelectorAll("[data-search-panel]")) {
      panel.classList.toggle(
        "is-active",
        panel.getAttribute("data-search-panel") === name,
      );
    }
  }

  handleOpen() {
    requestAnimationFrame(() => this.input?.focus());
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.countAbortController?.abort();
    super.disconnectedCallback();
  }

  handleInput(event) {
    const query = event.target.value.trim();
    this.updateQueryState(query);

    clearTimeout(this.searchTimeout);
    this.searchTimeout = window.setTimeout(() => {
      void this.fetchResults(query);
      void this.fetchCount(query);
    }, this.debounceDelay);
  }

  handleClear() {
    this.abortController?.abort();
    clearTimeout(this.searchTimeout);
    this.input.value = "";
    this.results.innerHTML = "";
    this.updateQueryState("");
    this.input.focus();
  }

  async fetchResults(query) {
    this.abortController?.abort();

    if (!query) {
      this.results.innerHTML = "";
      return;
    }

    this.resultsEmpty = false;
    this.setLoadingState();

    this.abortController = new AbortController();

    const url = new URL(this.predictiveSearchUrl, window.location.origin);
    url.searchParams.set("q", query);
    url.searchParams.set("section_id", this.sectionId);
    url.searchParams.set("resources[type]", "product,collection,article");
    url.searchParams.set("resources[limit]", "10");
    url.searchParams.set("resources[limit_scope]", "each");

    try {
      const response = await fetch(url.toString(), {
        signal: this.abortController.signal,
      });

      if (!response.ok) throw new Error("Predictive search request failed");

      const html = await response.text();
      this.renderSectionResults(html, query);
    } catch (error) {
      if (error.name === "AbortError") return;

      this.showResultsError();
    }
  }

  showResultsError() {
    this.resultsEmpty = false;
    this.results.innerHTML = `
      <p class="search-results__status">${this.escapeHtml(this.errorText)}</p>
    `;
  }

  async fetchCount(query) {
    this.countAbortController?.abort();

    if (!query || !this.viewAll) return;

    if (this.countFailed) this.setCountFailed(false);

    this.countAbortController = new AbortController();

    const url = new URL(this.sectionSearchUrl, window.location.origin);
    url.searchParams.set("q", query);
    url.searchParams.set("type", "product");
    url.searchParams.set("section_id", this.sectionId);

    try {
      const response = await fetch(url.toString(), {
        signal: this.countAbortController.signal,
      });

      if (!response.ok) throw new Error("Search count request failed");

      const html = await response.text();
      const parser = new DOMParser();
      const documentFragment = parser.parseFromString(html, "text/html");
      const total = documentFragment
        .querySelector('[data-ref="search-drawer.total"]')
        ?.textContent.trim();

      // Shopify can return an error message in place of the count.
      if (!total || !/\d/.test(total)) {
        throw new Error("Search count response had no result count");
      }

      this.viewAll.textContent = total;
    } catch (error) {
      if (error.name === "AbortError") return;

      this.setCountFailed(true);
      if (this.resultsEmpty) this.showResultsError();
    }
  }

  setLoadingState() {
    this.results.innerHTML = `
      <p class="search-results__status ">${this.escapeHtml(this.loadingText)}</p>
    `;
  }

  renderSectionResults(html, query) {
    const parser = new DOMParser();
    const documentFragment = parser.parseFromString(html, "text/html");
    const renderedResults = documentFragment.querySelector(".search-results");

    if (renderedResults) {
      this.results.innerHTML = renderedResults.innerHTML;
      return;
    }

    if (this.countFailed) {
      this.showResultsError();
      return;
    }

    this.resultsEmpty = true;
    this.results.innerHTML = `
      <p class="search-results__status ">${this.escapeHtml(this.interpolateQuery(this.noResultsTemplate, query))}</p>
    `;
  }

  escapeHtml(value = "") {
    const input = String(value);

    return input
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#39;");
  }

  get debounceDelay() {
    return 300;
  }

  get loadingText() {
    return this.getAttribute("loading-text") || "Loading...";
  }

  get retryText() {
    return this.getAttribute("retry-text") || "Try again";
  }

  get noResultsTemplate() {
    return (
      this.getAttribute("no-results-template") ||
      'No results found for "[query]".'
    );
  }

  get errorText() {
    return this.getAttribute("error-text") || "Unable to load results.";
  }

  get resultsTitleTemplate() {
    return (
      this.getAttribute("results-title-template") || 'Results for "[query]"'
    );
  }

  interpolateQuery(template, query) {
    return String(template)
      .replaceAll("[query]", query)
      .replaceAll("{{ terms }}", query);
  }

  updateQueryState(query) {
    const hasQuery = Boolean(query);
    if (!hasQuery) this.countFailed = false;
    this.drawer.classList.toggle("has-query", hasQuery);
    this.drawer.classList.toggle("has-input-value", hasQuery);

    if (this.viewAll) {
      const url = new URL(this.sectionSearchUrl, window.location.origin);
      if (hasQuery) {
        url.searchParams.set("q", query);
        url.searchParams.set("type", "product");
      } else {
        this.viewAll.textContent = this.defaultViewAllText;
      }
      this.viewAll.href = url.toString();
    }
  }

  get sectionSearchUrl() {
    const endpoint = this.getAttribute("search-url");

    if (!endpoint) return `${window.location.origin}/search`;

    return endpoint;
  }

  get predictiveSearchUrl() {
    const endpoint = this.getAttribute("predictive-search-url");

    if (!endpoint) return `${window.location.origin}/search/suggest`;

    return endpoint;
  }

  get sectionId() {
    return this.getAttribute("section-id");
  }
}

if (!customElements.get("search-drawer")) {
  customElements.define("search-drawer", SearchDrawer);
}
